import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { AuthLayout } from '@/components/AuthLayout';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
const schema = z.object({
    email: z.string().email(),
    password: z.string().min(8),
});
export function LoginPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const setAccessToken = useAuthStore((s) => s.setAccessToken);
    const [serverError, setServerError] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues: { email: '', password: '' },
    });
    const mutation = useMutation({
        mutationFn: async (values) => {
            const res = await api.post('/auth/token/', values);
            return res.data;
        },
        onSuccess: (data) => {
            if (data.two_factor_required && data.challenge_token) {
                navigate('/2fa', { state: { challengeToken: data.challenge_token } });
                return;
            }
            if (data.access) {
                setAccessToken(data.access);
                const from = location.state?.from?.pathname ?? '/';
                navigate(from, { replace: true });
            }
        },
        onError: (err) => {
            setServerError(err.response?.data?.error?.message ?? 'Sign-in failed.');
        },
    });
    return (_jsx(AuthLayout, { title: t('auth.login'), subtitle: "Welcome back.", footer: _jsxs("span", { children: ["Don't have an account?", ' ', _jsx(Link, { to: "/register", className: "font-medium text-foreground hover:underline", children: "Create one" })] }), children: _jsxs("form", { className: "space-y-4", onSubmit: form.handleSubmit((v) => mutation.mutate(v)), children: [_jsx(Field, { id: "email", type: "email", autoComplete: "email", label: t('auth.email'), error: form.formState.errors.email?.message, register: form.register('email') }), _jsx(Field, { id: "password", type: showPassword ? 'text' : 'password', autoComplete: "current-password", label: t('auth.password'), error: form.formState.errors.password?.message, register: form.register('password'), adornment: _jsx("button", { type: "button", tabIndex: -1, onClick: () => setShowPassword((v) => !v), "aria-label": showPassword ? 'Hide password' : 'Show password', className: "absolute end-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground", children: showPassword ? _jsx(EyeOff, { className: "h-4 w-4" }) : _jsx(Eye, { className: "h-4 w-4" }) }) }), _jsx("div", { className: "flex justify-end", children: _jsx(Link, { to: "/forgot-password", className: "text-xs text-muted-foreground hover:text-foreground hover:underline", children: "Forgot password?" }) }), serverError && _jsx("p", { className: "text-xs text-destructive", children: serverError }), _jsx("button", { type: "submit", className: "w-full rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50", disabled: mutation.isPending, children: mutation.isPending ? 'Signing in…' : t('auth.submit') })] }) }));
}
function Field({ id, type, label, error, register, autoComplete, adornment, }) {
    return (_jsxs("div", { className: "space-y-2", children: [_jsx("label", { htmlFor: id, className: "text-sm font-medium", children: label }), _jsxs("div", { className: "relative", children: [_jsx("input", { id: id, type: type, autoComplete: autoComplete, className: `block w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring ${adornment ? 'pe-10' : ''}`, ...register }), adornment] }), error && _jsx("p", { className: "text-xs text-destructive", children: error })] }));
}
